import type { WidgetSize } from '@tyndo/types';
import type { WidgetDefinition } from '../components/LandingPageEditor/types';

/**
 * Map a raw `/widgets/definitions` row (backend contract) onto the UI's
 * `WidgetDefinition` shape. The backend uses `displayName` +
 * `supportedSizesJson`; the UI uses `label` + `supportedSizes`. This is the
 * single place that bridges the two so widget NAMES render in the gallery,
 * canvas, and configure drawer instead of bare `widgetId`s.
 */
export function mapApiDefinition(row: unknown): WidgetDefinition {
  const r = (row ?? {}) as Record<string, unknown>;

  const widgetId = String(r.widgetId ?? r.id ?? '');
  const label = String(r.displayName ?? r.label ?? widgetId);

  return {
    widgetId,
    label,
    componentName:
      typeof r.componentName === 'string' ? r.componentName : undefined,
    description: typeof r.description === 'string' ? r.description : undefined,
    category: typeof r.category === 'string' ? r.category : undefined,
    moduleId: typeof r.moduleId === 'string' ? r.moduleId : undefined,
    supportedSizes: parseSupportedSizes(r),
    defaultSize:
      typeof r.defaultSize === 'string'
        ? (r.defaultSize as WidgetSize)
        : undefined,
    personaDefaults: asStringArray(r.personaDefaults),
    industryDefaults: asStringArray(r.industryDefaults),
  };
}

/**
 * Unwrap a `/widgets/definitions` payload (bare array or
 * `{ items | entries | data | results: [...] }` envelope) and map every row
 * to a `WidgetDefinition`. Returns `[]` for anything unrecognised.
 */
export function mapApiDefinitions(payload: unknown): WidgetDefinition[] {
  return unwrap(payload).map(mapApiDefinition);
}

// ── helpers ──────────────────────────────────────────────────────────────

function unwrap(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    const candidate = obj.items ?? obj.entries ?? obj.data ?? obj.results;
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
}

const VALID_SIZES: ReadonlySet<string> = new Set<WidgetSize>([
  'small',
  'medium',
  'large',
  'extraLarge',
  'wallboard',
]);

function parseSupportedSizes(r: Record<string, unknown>): WidgetSize[] | undefined {
  // Prefer the JSON-encoded field from the backend; fall back to an
  // already-decoded `supportedSizes` array if present.
  const raw = r.supportedSizesJson;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      const sizes = asSizeArray(parsed);
      if (sizes.length) return sizes;
    } catch {
      // fall through to the decoded field
    }
  }
  const decoded = asSizeArray(r.supportedSizes);
  return decoded.length ? decoded : undefined;
}

function asSizeArray(value: unknown): WidgetSize[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (s): s is WidgetSize => typeof s === 'string' && VALID_SIZES.has(s),
  );
}

function asStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value.filter((s): s is string => typeof s === 'string');
  return out.length ? out : undefined;
}
